// =====================================================================
// Assessment Center — Student Panel
// Available Exams page — exams open to the student right now (within
// their scheduling window, with attempts remaining).
// =====================================================================
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, EmptyState, PageHeader, Spinner } from "../../../shared/components/ui";
import { listAvailableExams, startAttempt, type AvailableExam } from "../lib/mock-api";

const examTypeLabel: Record<string, string> = {
  multiple_choice: "Multiple Choice",
  essay: "Essay",
  mixed: "Mixed",
};

export default function AvailableExamsPage() {
  const [exams, setExams] = useState<AvailableExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    listAvailableExams().then((data) => {
      setExams(data);
      setLoading(false);
    });
  }, []);

  async function handleOpen(exam: AvailableExam) {
    setError(null);
    setStartingId(exam.id);
    try {
      const runner = await startAttempt(exam.id);
      navigate(`/student/assessment-center/exams/${runner.attempt.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't open this exam.");
      setStartingId(null);
    }
  }

  return (
    <div>
      <PageHeader title="Available Exams" subtitle="Exams open for you to take right now." />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-slate-500">
          <Spinner /> Loading exams…
        </div>
      ) : exams.length === 0 ? (
        <EmptyState
          title="No exams available right now"
          description="Check back once your instructor publishes a new exam, or see My Exams for exams you've already started."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {exams.map((exam) => (
            <Card key={exam.id} className="flex flex-col p-5">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-base font-semibold text-navy-900">{exam.title}</h3>
                <Badge tone="navy">{examTypeLabel[exam.exam_type]}</Badge>
              </div>
              {exam.description && (
                <p className="mt-2 line-clamp-2 text-sm text-slate-500">{exam.description}</p>
              )}
              <dl className="mt-4 space-y-1 text-xs text-slate-500">
                <div className="flex justify-between">
                  <dt>Questions</dt>
                  <dd>{exam.question_count}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Duration</dt>
                  <dd>{exam.duration_minutes} min</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Attempts</dt>
                  <dd>
                    {exam.attempts_used} / {exam.max_attempts} used
                  </dd>
                </div>
              </dl>
              <div className="mt-4 flex-1" />
              <Button
                className="w-full"
                disabled={startingId === exam.id}
                onClick={() => handleOpen(exam)}
              >
                {startingId === exam.id
                  ? "Opening…"
                  : exam.has_in_progress_attempt
                    ? "Resume Exam"
                    : "Open Exam"}
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
